/**
 * Search results list — reads hits from `SearchAppContext`.
 */
import { useCallback, useContext } from 'react';
import {
  SearchAppContext,
  SearchAppDispatchContext,
} from '../../context/search-app/search-app-context';
import { CLEAR_FILTERS, SET_QUERY } from '../../context/search-app/search-app-actions';
import SearchResult from '../search-result';
import SearchResultsSkeleton from '../search-results-skeletons';

export const BASE_CLASS = 'search-results';

import { hasActiveFacets } from '../../context/search-app/has-active-facets';
const SearchResults = () => {
  const { results, status, error, query, selectedFacets } =
    useContext(SearchAppContext);
  const { dispatch } = useContext(SearchAppDispatchContext);

  const handleRetry = useCallback(() => {
    dispatch({ type: SET_QUERY, payload: query });
  }, [dispatch, query]);

  const handleClearFilters = useCallback(() => {
    dispatch({ type: CLEAR_FILTERS });
  }, [dispatch]);

  switch (true) {
    case status === 'loading':
      return (
        <div role="status" aria-busy="true" aria-live="polite">
          <SearchResultsSkeleton listClassName={BASE_CLASS} />
        </div>
      );

    case status === 'error' && Boolean(error):
      return (
        <div
          className={`${BASE_CLASS}__status ${BASE_CLASS}__status--error`}
          role="alert"
        >
          <p>{error}</p>
          <button
            type="button"
            className={`${BASE_CLASS}__retry`}
            onClick={handleRetry}
          >
            Try again
          </button>
        </div>
      );

    case status === 'success' && results.length === 0:
      return (
        <div className={`${BASE_CLASS}__status`} role="status">
          <p>No results found.</p>
          {hasActiveFacets(selectedFacets) ? (
            <button
              type="button"
              className={`btn btn--charcoal btn--secondary btn--medium ${BASE_CLASS}__clear-filters`}
              onClick={handleClearFilters}
            >
              Clear all filters
            </button>
          ) : null}
        </div>
      );

    default:
      return (
        <ul className={BASE_CLASS} aria-label="Search results">
          {results.map((result, index) => (
            <li key={`${result.url}-${index}`} className={`${BASE_CLASS}__item`}>
              <SearchResult result={result} />
            </li>
          ))}
        </ul>
      );
  }
};

export default SearchResults;
